import React, { useEffect, useState } from "react";

import { cpf } from "cpf-cnpj-validator";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";

import {
  Col,
  Form,
  Row,
  VertButton,
  VertContainer,
  VertInput,
  VertSelect,
  VertTypography,
  notification,
} from "@vertem-core/vertice-ds";

import "Assets/styles/default/components/Product/QuotationModal/AgroInsurance.less";

import usePipeContext from "Services/Providers/PipeProvider/usePipeContext";

import { QUOTATION_FORM_TYPE } from "Constants/QuotationFormConstants";

import { StateList } from "Utils/commons/address";
import generateUUID from "Utils/generateUUID";

const AgroInsuranceQuotationFormModal = ({
  setIsModalOpened,
  product,
  vendorId,
  sku,
}) => {
  const quotation = useSelector(({ Quotation }) => Quotation);

  useEffect(() => {
    if (quotation.isError) {
      notification.error({
        message: "Não foi possível registrar a cotação.",
      });
    }

    if (quotation.isLoaded) {
      useForm.resetFields();

      setIsModalOpened(false);

      notification.success({
        message: "Cotação realizada com sucesso.",
      });
    }
  }, [quotation]);

  const handleCpfValidation = async (_, value) => {
    if (!value || cpf.isValid(value)) {
      return Promise.resolve();
    }

    return Promise.reject();
  };

  const formFields = {
    name: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    cpf: {
      rules: [
        { required: true, message: "Campo obrigatório." },
        { validator: handleCpfValidation, message: "CPF Inválido." },
      ],
    },
    city: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    state: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    area: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    culture: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    productionCost: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
    bagValue: {
      rules: [{ required: true, message: "Campo obrigatório." }],
    },
  };

  const [useForm] = Form.useForm();

  const { callApi } = usePipeContext();

  const userProfile = useSelector(({ oidc }) => oidc.user.profile);

  function handleFormSubmit() {
    useForm
      .validateFields()
      .then(() => {
        let quotationQtd = useForm.getFieldValue("area");

        quotationQtd = quotationQtd.replace(/\D/g, '');

        const products = [
          {
            name: product,
            sku,
            quantity: Number(quotationQtd),
            unitPrice: 0,
            vendorId: String(vendorId),
            originalId: String(sku),
            formId: String(QUOTATION_FORM_TYPE.AGRO_INSURANCE_QUOTATION),
          },
        ];

        const keyMap = {
          name: "Nome",
          cpf: "CPF",
          city: "Município",
          state: "UF",
          area: "Área (ha)",
          culture: "Cultura",
          productionCost: "Custo/ha",
          bagValue: "Valor de saca",
        };

        const fields = Object.keys(formFields);

        const params = fields.map((field) => ({
          key: keyMap[field],
          value: useForm.getFieldValue(field),
        }));

        callApi({
          symbol: "ADD_QUOTATION",
          alias: generateUUID(),
          payload: {
            products,
            supplierId: vendorId,
            participantId: userProfile["custom:participant_id"],
            params,
          },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  // value and onChange props are injected by <Form.Item />
  const CustomNumericInput = ({
    name,
    label,
    value,
    onChange,
    ...numericProps
  }) => {
    const [isFocused, setIsFocused] = useState(value ? true : false);

    function handleChange({ formattedValue }) {
      onChange(formattedValue);
    }

    function handleBlur() {
      if (!value) setIsFocused(false);

      useForm.validateFields([name]);
    }

    return (
      <div className={`vert-input-container ${isFocused ? "focused" : ""}`}>
        <label className="vert-input-label">{label}</label>
        <NumericFormat
          {...numericProps}
          value={value}
          className="ant-input ant-input-outlined vert-input vert-shape-default vert-template-marketplace ant-input-size-default padding-x-undefined"
          displayType={"input"}
          onFocus={() => setIsFocused(true)}
          onValueChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
    );
  };

  return (
    <VertContainer className="agro-insurance">
      <Form form={useForm}>
        <Row verticalSpacing="small">
          <Col>
            <VertTypography.Paragraph strong>
              Dados pessoais
            </VertTypography.Paragraph>
          </Col>
        </Row>

        <Row gutter={[0, 8]}>
          <Col span={24}>
            <Form.Item
              name="name"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.name.rules}
            >
              <VertInput label="Nome*" maxLength={256}></VertInput>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="cpf"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.cpf.rules}
            >
              <VertInput
                className="cpf-mask-color"
                label="CPF*"
                placeholder=""
                mask="000.000.000-00"
                maskOptions={{ lazy: true }}
              ></VertInput>
            </Form.Item>
          </Col>
        </Row>

        <Row verticalSpacing="small">
          <Col>
            <VertTypography.Paragraph strong>
              Dados da propriedade
            </VertTypography.Paragraph>
          </Col>
        </Row>

        <Row
          gutter={[
            { xs: 0, md: 8 },
            { xs: 8, md: 0 },
          ]}
        >
          <Col xs={24} md={12}>
            <Form.Item
              name="city"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.city.rules}
            >
              <VertInput label="Município*" maxLength={256}></VertInput>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="state"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.state.rules}
            >
              <VertSelect
                className="state-selector"
                placeholder="UF*"
                items={StateList}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 0]}>
          <Col span={12}>
            <Form.Item
              name="area"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.area.rules}
            >
              <CustomNumericInput
                name="area"
                label="Área (ha)*"
                thousandSeparator={"."}
                decimalSeparator={','}
                decimalScale={0}
                suffix={" ha"}
                allowNegative={false}
                isNumericString={false}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="culture"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.culture.rules}
            >
              <VertInput label="Cultura*"></VertInput>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 0]}>
          <Col span={12}>
            <Form.Item
              name="productionCost"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.productionCost.rules}
            >
              <CustomNumericInput
                name="productionCost"
                label="Custo/ha*"
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"R$ "}
                fixedDecimalScale={true}
                decimalScale={2}
                allowNegative={false}
                isNumericString={false}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="bagValue"
              validateTrigger={["onBlur", "onChange"]}
              rules={formFields.bagValue.rules}
            >
              <CustomNumericInput
                name="bagValue"
                label="Valor de saca*"
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"R$ "}
                fixedDecimalScale={true}
                decimalScale={2}
                allowNegative={false}
                isNumericString={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end" verticalSpacing="small">
          <Col>
            <VertButton
              type="highlight"
              loading={quotation.isLoading}
              onClick={handleFormSubmit}
            >
              Solicitar Cotação
            </VertButton>
          </Col>
        </Row>
      </Form>
    </VertContainer>
  );
};

export default AgroInsuranceQuotationFormModal;
